import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap, throwError } from 'rxjs';

import { apiPaths } from '../../app/core/constants/api.const';
import { AppUtilityService } from './app-utility.service';
import {
  StudyLinkEditRequest,
  StudyLinkResponse,
  StudyLinksApiResponse
} from '../models/study-link.model';
import { toStudyLinkResponse } from '../utils/study-link.util';

/**
 * StudyLinksService
 *
 * The linkages between studies, fetched once and shared — the counterpart of
 * StudyLogbookService, which supplies the studies those linkages join.
 *
 * The whole edge list ships in one payload because the Linked Studies picker shows a link
 * count against every study it lists; per-study reads could only answer one row at a time.
 * There is one row per linkage, so the list stays small.
 */
@Injectable({ providedIn: 'root' })
export class StudyLinksService {
  private links$: Observable<StudyLinkResponse> | null = null;

  constructor(private readonly appUtilityService: AppUtilityService) {}

  /** Every linkage, fetched on first call and replayed to everyone after. */
  load(): Observable<StudyLinkResponse> {
    if (!this.links$) {
      this.links$ = this.appUtilityService
        .get<StudyLinksApiResponse>(this.appUtilityService.getAPI(apiPaths.study.links))
        .pipe(
          map(toStudyLinkResponse),
          // shareReplay would otherwise hand the same failure to every later subscriber, so a
          // failed fetch drops the cache and the next caller starts a fresh request.
          catchError(error => {
            this.links$ = null;
            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.links$;
  }

  /** Discards the cached payload and fetches again — the Retry path. */
  reload(): Observable<StudyLinkResponse> {
    this.links$ = null;
    return this.load();
  }

  /**
   * Drops the cached payload without fetching — the path taken when a save elsewhere in the app
   * has changed the linkages, since `related_study_names` on a save replaces a study's lot.
   *
   * No signal is raised here: a save invalidates the logbook too, and the only view of the
   * linkages redraws off StudyLogbookService.invalidated$, refetching both halves at once.
   */
  invalidate(): void {
    this.links$ = null;
  }

  /**
   * Links two studies. Adds the one pair and leaves the study's other linkages alone, unlike
   * `related_study_names` on a save, which replaces the lot.
   *
   * `loadedStudyName` names the study the app has open, if any: a linkage change is recorded
   * on both studies, so when the open one is part of the pair its refreshed audit trail comes
   * back on the response.
   */
  add(
    studyName: string,
    relatedStudyName: string,
    loadedStudyName?: string
  ): Observable<StudyLinkResponse> {
    return this.edit(apiPaths.study.addLink, studyName, relatedStudyName, loadedStudyName);
  }

  /** Removes the linkage between two studies, leaving every other one intact. */
  remove(
    studyName: string,
    relatedStudyName: string,
    loadedStudyName?: string
  ): Observable<StudyLinkResponse> {
    return this.edit(apiPaths.study.removeLink, studyName, relatedStudyName, loadedStudyName);
  }

  private edit(
    path: string,
    studyName: string,
    relatedStudyName: string,
    loadedStudyName?: string
  ): Observable<StudyLinkResponse> {
    const body: StudyLinkEditRequest = {
      study_name: studyName,
      related_study_name: relatedStudyName
    };
    if (loadedStudyName) {
      body.loaded_study_name = loadedStudyName;
    }

    return this.appUtilityService
      .post<StudyLinkEditRequest, StudyLinksApiResponse>(
        this.appUtilityService.getAPI(path),
        body
      )
      .pipe(
        map(toStudyLinkResponse),
        // Both edits answer with every linkage afterwards, so the cache is replaced from the
        // response rather than invalidated — no follow-up fetch, and no window where the two
        // Studies tabs disagree about what is linked. A failed edit leaves it untouched.
        //
        // Only the linkages are cached: an edit's response may also carry the loaded study's
        // audit trail, which is a snapshot of that one moment and must not be replayed to a
        // later subscriber as though it were current.
        tap(response => {
          this.links$ = of({ links: response.links });
        })
      );
  }
}
